import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import NotFound from '../app/NotFound';
import { DEMO_CONFIGS } from '../../config/demoConfig';
import {
  ORG_CONFIGS,
  SupportedOrgs,
  isSupportedOrg,
} from '../../config/storeConfig';
import { IframeToastType } from '../../utils/iframe/postMessage';

const INITIAL_IFRAME_HEIGHT = 600;
const TOAST_DURATION_MS = 4000;

const TOAST_COLORS: Record<IframeToastType, string> = {
  success: '#53ad20',
  info: '#3498db',
  warning: '#f1c40f',
  error: '#e74c3c',
};

function buildDemoIframeSrc(
  collectionHandle: string,
  demo: { primaryColor: string; textColorOverride: string; navbar?: string },
  search: string
): string {
  const params = new URLSearchParams(search);
  const navbarParam = demo.navbar ? `&navbar=${demo.navbar}` : '';
  const suffix = `?primaryColor=${demo.primaryColor}&textColorOverride=${demo.textColorOverride}${navbarParam}`;

  if (params.get('cart') === '1') {
    return `/cart${suffix}`;
  }

  const product = params.get('product');
  if (product) {
    return `/product/${encodeURIComponent(product)}${suffix}`;
  }

  return `/collection/${encodeURIComponent(collectionHandle)}${suffix}`;
}

/** Accept a Shopify handle or a pasted /collections/{handle} URL. */
function collectionHandleFromInput(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  try {
    const url = trimmed.includes('://') ? new URL(trimmed) : null;
    if (url) {
      const parts = url.pathname.split('/').filter(Boolean);
      const collectionsIdx = parts.findIndex(p => p === 'collections' || p === 'collection');
      const handle = collectionsIdx >= 0 ? parts[collectionsIdx + 1] : parts[parts.length - 1];
      return handle ? decodeURIComponent(handle) : undefined;
    }
  } catch {
    return undefined;
  }

  const slug = trimmed.replace(/^\/+|\/+$/g, '').split('/').pop();
  return slug || undefined;
}

function demoUrl(
  demoPath: string,
  params: { collection?: string; product?: string; cart?: boolean },
  defaultHandle: string
): string {
  const search = new URLSearchParams();
  if (params.collection && params.collection !== defaultHandle) {
    search.set('collection', params.collection);
  }
  if (params.product) {
    search.set('product', params.product);
  }
  if (params.cart) {
    search.set('cart', '1');
  }
  const qs = search.toString();
  return qs ? `${demoPath}?${qs}` : demoPath;
}

function parentUrlFromNavigation(
  data: { pathname?: string; productHandle?: string; collectionHandle?: string },
  demoPath: string,
  defaultHandle: string,
  embedCollection: string
): string {
  if (data.pathname === '/cart') {
    return demoUrl(demoPath, { collection: embedCollection, cart: true }, defaultHandle);
  }
  if (data.productHandle) {
    return demoUrl(
      demoPath,
      { collection: embedCollection, product: data.productHandle },
      defaultHandle
    );
  }
  const collection = data.collectionHandle || embedCollection;
  return demoUrl(demoPath, { collection }, defaultHandle);
}

const DemoParent: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const loadedCollectionRef = useRef<string | undefined>();
  const loadedOrgRef = useRef<string | undefined>();
  const embedCollectionRef = useRef<string>('');
  const [height, setHeight] = useState<number>(INITIAL_IFRAME_HEIGHT);
  const [parentToast, setParentToast] = useState<{
    toastType: IframeToastType;
    message: string;
  } | null>(null);
  const [parentUrlLabel, setParentUrlLabel] = useState(
    () => window.location.pathname + window.location.search
  );
  const [iframeSrc, setIframeSrc] = useState(() => {
    const pathOrg = window.location.pathname.split('/')[2];
    if (!isSupportedOrg(pathOrg)) return '';
    const initialDemo = DEMO_CONFIGS[pathOrg];
    const initialDefault = initialDemo?.collectionHandles[0];
    if (!initialDemo || !initialDefault) return '';
    const initialHandle =
      new URLSearchParams(window.location.search).get('collection') || initialDefault;
    loadedCollectionRef.current = initialHandle;
    loadedOrgRef.current = pathOrg;
    embedCollectionRef.current = initialHandle;
    return buildDemoIframeSrc(initialHandle, initialDemo, window.location.search);
  });
  const [draftHandle, setDraftHandle] = useState('');

  const validOrgId: SupportedOrgs | undefined = isSupportedOrg(orgId) ? orgId : undefined;
  const demo = validOrgId ? DEMO_CONFIGS[validOrgId] : undefined;
  const org = validOrgId ? ORG_CONFIGS[validOrgId] : undefined;
  const defaultHandle = demo?.collectionHandles[0];
  const demoPath = validOrgId ? `/demo/${validOrgId}` : '';
  const collectionHandles = demo?.collectionHandles ?? [];
  const collectionParam = searchParams.get('collection');
  const activeHandle = collectionParam || defaultHandle || '';

  useEffect(() => {
    if (!demo || !defaultHandle || !validOrgId) return;
    const handle = collectionParam || defaultHandle;
    if (loadedOrgRef.current === validOrgId && loadedCollectionRef.current === handle) {
      return;
    }
    const searchForSrc =
      loadedOrgRef.current === undefined ? window.location.search : '';
    loadedOrgRef.current = validOrgId;
    loadedCollectionRef.current = handle;
    embedCollectionRef.current = handle;
    setDraftHandle(handle);
    setIframeSrc(buildDemoIframeSrc(handle, demo, searchForSrc));
    setHeight(INITIAL_IFRAME_HEIGHT);
  }, [demo, defaultHandle, collectionParam, validOrgId]);

  useEffect(() => {
    if (!demo || !validOrgId || !defaultHandle) return;

    const orgContext = {
      orgId: validOrgId,
      contactId: demo.contactId,
      salesforceId: demo.salesforceId,
      primaryColor: demo.primaryColor,
      textColorOverride: demo.textColorOverride,
      navbar: demo.navbar,
    };

    // Ask the widget to traverse its own history. Never touch
    // `contentWindow.history` — that only works while the demo happens to be
    // same-origin, and throws on a real cross-origin embed.
    const onPopstate = () => {
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'enrichly:back' },
        window.location.origin
      );
      setParentUrlLabel(window.location.pathname + window.location.search);
    };

    const mirrorNavigation = (data: {
      pathname?: string;
      productHandle?: string;
      collectionHandle?: string;
    }) => {
      if (data.collectionHandle) {
        embedCollectionRef.current = data.collectionHandle;
      }
      const nextUrl = parentUrlFromNavigation(
        data,
        demoPath,
        defaultHandle,
        embedCollectionRef.current
      );
      const currentUrl = window.location.pathname + window.location.search;

      // The widget's `navigation` message lands a task later than the
      // `enrichly:back` that caused it, so a timing latch cannot gate this.
      // Comparing URLs is what actually prevents the duplicate history entry.
      if (currentUrl === nextUrl) {
        setParentUrlLabel(currentUrl);
        return;
      }

      navigate(nextUrl);
      setParentUrlLabel(nextUrl);
    };

    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;

      switch (data.type) {
        case 'enrichly:ready':
          iframeRef.current?.contentWindow?.postMessage(
            {
              type: 'enrichly:context',
              context: {
                ...orgContext,
                collectionHandle: embedCollectionRef.current || defaultHandle,
              },
            },
            '*'
          );
          break;
        case 'resize':
          if (typeof data.height === 'number' && data.height > 0) {
            setHeight(data.height);
          }
          break;
        case 'enrichly:bulkOrder':
          if (data.url) window.open(data.url, '_blank', 'noopener,noreferrer');
          break;
        case 'enrichly:toast':
          if (data.message) {
            setParentToast({
              toastType: data.toastType ?? 'info',
              message: data.message,
            });
          }
          break;
        case 'navigation':
          if (data.pathname) {
            mirrorNavigation(data);
          }
          break;
      }
    };

    window.addEventListener('popstate', onPopstate);
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('popstate', onPopstate);
      window.removeEventListener('message', onMessage);
    };
  }, [demo, validOrgId, demoPath, defaultHandle, navigate]);

  useEffect(() => {
    if (!parentToast) return;
    const timer = window.setTimeout(() => setParentToast(null), TOAST_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [parentToast]);

  const loadCollection = (raw: string) => {
    const handle = collectionHandleFromInput(raw);
    if (!handle || !defaultHandle) return;
    const next = new URLSearchParams();
    if (handle !== defaultHandle) {
      next.set('collection', handle);
    }
    setSearchParams(next, { replace: true });
    setParentUrlLabel(demoUrl(demoPath, { collection: handle }, defaultHandle));
  };

  const onCustomSubmit = (event: FormEvent) => {
    event.preventDefault();
    loadCollection(draftHandle);
  };

  if (!validOrgId || !demo || !org || !defaultHandle) {
    return <NotFound />;
  }

  const cssVars = {
    ['--demo-org' as string]: `#${demo.primaryColor}`,
    ['--demo-org-text' as string]: `#${demo.textColorOverride}`,
  } as React.CSSProperties;

  return (
    <div className="enrichly-demo" style={cssVars}>
      <style>{`
        .enrichly-demo {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          background: #f4f4f2;
          color: #091b1f;
          min-height: 100vh;
        }
        .enrichly-demo * { box-sizing: border-box; }
        .enrichly-demo .demo-topbar {
          align-items: center;
          background: var(--demo-org);
          color: var(--demo-org-text);
          display: flex;
          justify-content: space-between;
          padding: 14px 24px;
        }
        .enrichly-demo .demo-topbar h1 {
          font-size: 18px;
          letter-spacing: 0.05em;
          margin: 0;
        }
        .enrichly-demo .demo-topbar .demo-user { font-size: 13px; opacity: 0.85; }
        .enrichly-demo .demo-nav {
          background: #fff;
          border-bottom: 1px solid #e0e0e0;
          display: flex;
          gap: 20px;
          padding: 10px 24px;
        }
        .enrichly-demo .demo-nav a {
          color: #333;
          font-size: 14px;
          font-weight: 500;
          text-decoration: none;
        }
        .enrichly-demo .demo-nav a[aria-current='page'] { color: var(--demo-org); }
        .enrichly-demo .demo-main { padding: 24px; }
        .enrichly-demo .demo-section-title {
          font-size: 14px;
          letter-spacing: 0.05em;
          margin: 0 0 12px;
          text-transform: uppercase;
          color: #555;
        }
        .enrichly-demo .demo-section-title--url {
          color: #999;
          font-weight: 400;
          letter-spacing: 0;
          text-transform: none;
        }
        .enrichly-demo .demo-collection-bar {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin: 0 0 16px;
        }
        .enrichly-demo .demo-collection-bar label {
          color: #555;
          font-size: 12px;
          font-weight: 600;
        }
        .enrichly-demo .demo-collection-bar select,
        .enrichly-demo .demo-collection-bar input {
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 13px;
          padding: 6px 8px;
        }
        .enrichly-demo .demo-collection-bar input {
          flex: 1;
          min-width: 220px;
        }
        .enrichly-demo .demo-collection-bar button {
          background: var(--demo-org);
          border: 0;
          border-radius: 4px;
          color: var(--demo-org-text);
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          padding: 6px 12px;
        }
        .enrichly-demo .demo-org-links {
          display: flex;
          gap: 12px;
          margin: 0 0 12px;
        }
        .enrichly-demo .demo-org-links a {
          color: #333;
          font-size: 13px;
        }
        .enrichly-demo .demo-org-links a[aria-current='page'] {
          color: var(--demo-org);
          font-weight: 600;
        }
        .enrichly-demo .demo-store-frame {
          background: #fff;
          border-bottom: 1px solid #e0e0e0;
          border-top: 1px solid #e0e0e0;
          overflow: hidden;
        }
        .enrichly-demo .demo-store-frame iframe {
          border: 0;
          display: block;
          transition: height 0.2s ease;
          width: 100%;
        }
        .enrichly-demo .demo-footer {
          color: #888;
          font-size: 12px;
          padding: 24px;
          text-align: center;
        }
        .enrichly-demo .demo-footer strong { color: var(--demo-org); }
        .enrichly-demo .demo-parent-toast-host {
          bottom: 16px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          left: 16px;
          max-width: min(360px, calc(100vw - 32px));
          pointer-events: none;
          position: fixed;
          z-index: 2147483647;
        }
        .enrichly-demo .demo-parent-toast {
          align-items: stretch;
          background: #fff;
          border: 1px solid #d3cbdf;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          color: #091b1f;
          display: flex;
          font-size: 14px;
          line-height: 1.4;
          pointer-events: auto;
        }
        .enrichly-demo .demo-parent-toast__accent { width: 4px; }
        .enrichly-demo .demo-parent-toast__body { flex: 1; padding: 12px 16px; }
        .enrichly-demo .demo-parent-toast__close {
          background: transparent;
          border: 0;
          border-left: 1px solid #d3cbdf;
          color: inherit;
          cursor: pointer;
          font-size: 18px;
          line-height: 1;
          padding: 0 14px;
        }
      `}</style>

      {parentToast && (
        <div className="demo-parent-toast-host" aria-live="polite">
          <div className="demo-parent-toast" role="status">
            <div
              className="demo-parent-toast__accent"
              style={{ background: TOAST_COLORS[parentToast.toastType] }}
            />
            <div className="demo-parent-toast__body">{parentToast.message}</div>
            <button
              type="button"
              className="demo-parent-toast__close"
              aria-label="Dismiss notification"
              onClick={() => setParentToast(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <header className="demo-topbar">
        <h1>{org.name}</h1>
        <span className="demo-user">Signed in as {demo.signedInAs}</span>
      </header>

      <nav className="demo-nav">
        <a href="#home">Home</a>
        <a href="#members">{demo.membersLabel}</a>
        <a href="#events">Events</a>
        <a href="#store" aria-current="page">
          Store
        </a>
        <a href="#profile">Profile</a>
      </nav>

      <main className="demo-main">
        <p className="demo-section-title">Official {validOrgId.toUpperCase()} Store</p>
        <div className="demo-org-links">
          {Object.values(SupportedOrgs).map(id => (
            <Link
              key={id}
              to={`/demo/${id}`}
              aria-current={id === validOrgId ? 'page' : undefined}
            >
              {ORG_CONFIGS[id].name}
            </Link>
          ))}
        </div>
        <form className="demo-collection-bar" onSubmit={onCustomSubmit}>
          <label htmlFor="demo-collection-select">Collection</label>
          <select
            id="demo-collection-select"
            key={validOrgId}
            value={activeHandle}
            onChange={event => loadCollection(event.target.value)}
          >
            {collectionHandles.map(handle => (
              <option key={handle} value={handle}>
                {handle}
              </option>
            ))}
            {activeHandle && !collectionHandles.includes(activeHandle) && (
              <option value={activeHandle}>{activeHandle}</option>
            )}
          </select>
          <input
            aria-label="Collection handle or Shopify URL"
            placeholder="handle or Shopify collection URL"
            value={draftHandle}
            onChange={event => setDraftHandle(event.target.value)}
          />
          <button type="submit">Load</button>
        </form>
        <p className="demo-section-title demo-section-title--url">Parent URL: {parentUrlLabel}</p>
        <div className="demo-store-frame">
          <iframe
            key={`${validOrgId}:${activeHandle}`}
            ref={iframeRef}
            title={`${validOrgId.toUpperCase()} storefront`}
            src={iframeSrc}
            allow="payment; clipboard-write"
            style={{ height }}
          />
        </div>
      </main>

      <footer className="demo-footer">
        © {new Date().getFullYear()} <strong>Enrichly</strong> · {validOrgId.toUpperCase()} member
        portal demo
      </footer>
    </div>
  );
};

export default DemoParent;
